import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { useExperiences } from "@/hooks/useExperiences";
import { toast } from "@/hooks/use-toast";

const tipos = [
  { label: "CLT", value: "clt" },
  { label: "Autônomo", value: "autonomo" },
  { label: "Freela", value: "freela" },
  { label: "Projeto", value: "project" },
  { label: "Voluntariado", value: "voluntariado" },
  { label: "Formal", value: "formal" },
];

export default function AlunoNovaExperiencia() {
  const navigate = useNavigate();
  const { createExperience } = useExperiences();
  const [tipo, setTipo] = useState("");
  const [titulo, setTitulo] = useState("");
  const [org, setOrg] = useState("");
  const [descricao, setDescricao] = useState("");
  const [aprendizado, setAprendizado] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!tipo) newErrors.tipo = "Selecione um tipo";
    if (!titulo.trim()) newErrors.titulo = "Título é obrigatório";
    if (!aprendizado.trim()) newErrors.aprendizado = "Este campo é obrigatório";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await createExperience.mutateAsync({
        title: titulo,
        exp_type: tipo,
        org: org || null,
        description: descricao || null,
        learning_outcome: aprendizado,
      });
      toast({ title: "Experiência salva!" });
      navigate("/aluno/experiencia");
    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/aluno/experiencia")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading text-xl font-bold text-foreground">Nova Experiência</h1>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-6">
        <div className="space-y-2">
          <Label>Tipo de experiência</Label>
          <div className="flex flex-wrap gap-2">
            {tipos.map((t) => (
              <Badge key={t.value} variant={tipo === t.value ? "interest-selected" : "interest"}
                className="cursor-pointer" onClick={() => { setTipo(t.value); setErrors((p) => ({ ...p, tipo: "" })); }}>
                {tipo === t.value && <Check className="w-3 h-3" />}{t.label}
              </Badge>
            ))}
          </div>
          {errors.tipo && <p className="text-xs text-destructive">{errors.tipo}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="titulo">Título</Label>
          <Input id="titulo" placeholder="Ex: Projeto de TCC, Voluntariado na ONG..."
            value={titulo} onChange={(e) => { setTitulo(e.target.value); setErrors((p) => ({ ...p, titulo: "" })); }}
            className={errors.titulo ? "border-destructive" : ""} />
          {errors.titulo && <p className="text-xs text-destructive">{errors.titulo}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="org">Organização (opcional)</Label>
          <Input id="org" placeholder="Ex: Escola, ONG, Empresa..." value={org}
            onChange={(e) => setOrg(e.target.value)} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="descricao">O que você fez?</Label>
          <Textarea id="descricao" placeholder="Descreva suas atividades..." value={descricao}
            onChange={(e) => setDescricao(e.target.value)} rows={3} className="resize-none" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="aprendizado" className="flex items-center gap-2">
            O que isso me ensinou para o trabalho?
            <span className="text-xs text-destructive">*obrigatório</span>
          </Label>
          <Textarea id="aprendizado" placeholder="Ex: Aprendi a trabalhar em equipe, lidar com prazos..."
            value={aprendizado} onChange={(e) => { setAprendizado(e.target.value); setErrors((p) => ({ ...p, aprendizado: "" })); }}
            rows={3} className={errors.aprendizado ? "border-destructive resize-none" : "resize-none"} />
          {errors.aprendizado && <p className="text-xs text-destructive">{errors.aprendizado}</p>}
          <p className="text-xs text-muted-foreground">Essa reflexão é importante para mostrar seu valor.</p>
        </div>

        <div className="pt-4">
          <Button variant="hero" size="xl" className="w-full" onClick={handleSave} disabled={saving}>
            {saving ? (<><Loader2 className="w-5 h-5 animate-spin" />Salvando...</>) : "Salvar experiência"}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
